import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useAtomValue } from 'jotai';
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md';
import useFetchJobChangeProcess from '../../api/useFetchJobChangeProcess';
import { currentSelectjobChangeProcessAtom } from '../../atoms/jobChangeProcess';
import JobChangeProcessLogList from './JobChangeProcessLogList';
import { DetailField, DetailSection } from './JobChangeProcessDetailSection';
import Loading from '@/components/Loading';
import { Button } from '@/components/Button';
import useFetchIam from '@/features/agent/api/useFetchIam';
import { confirm } from '@/utils/confirm';

type Props = {
  closeSidebar: () => void;
  statusOpen: () => void;
  isSidebarOpen: boolean;
  // 未指定なら「提案まで戻す」フッターごと非表示（マッチング管理はFigma上フッターを持たない）
  onRollback?: () => void;
  isRollbackPending?: boolean;
};

export default function JobChangeProcessSidebar({
  closeSidebar,
  statusOpen,
  isSidebarOpen,
  onRollback,
  isRollbackPending = false,
}: Props) {
  const currentSelectjobChangeProcess = useAtomValue(currentSelectjobChangeProcessAtom);
  const [isCandidateDetailOpen, setIsCandidateDetailOpen] = useState(false);
  const { data, refetch, isLoading } = useFetchJobChangeProcess(
    currentSelectjobChangeProcess?.id ?? null
  );
  const { data: iam } = useFetchIam();
  const currentUser = iam?.current_user;
  const isAdmin = currentUser?.is_admin || currentUser?.is_support;
  const canRollback = data?.actions?.able_to_rollback_to_suggest && isAdmin;

  // 選考プロセスが選択されていない場合は何も表示しない
  if (!currentSelectjobChangeProcess) {
    return (
      <div className="flex h-full items-center justify-center bg-white">
        <p className="text-gray-500">選考プロセスを選択してください</p>
      </div>
    );
  }

  // ローディング中はローディングアニメーションを表示
  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center bg-white">
        <Loading size="medium" />
      </div>
    );
  }

  // データがない場合はエラーメッセージを表示
  if (!data) {
    return (
      <div className="flex h-full items-center justify-center bg-white">
        <p className="text-gray-500">データの読み込みに失敗しました</p>
      </div>
    );
  }

  const candidateProfile = data.candidate_profile;
  const providedJobPost = data.provided_job_post;
  const jobTitle = providedJobPost?.job_position_name ?? providedJobPost?.title ?? null;
  const jobUrl = providedJobPost?.urls?.show ?? providedJobPost?.urls?.open ?? null;
  const company = data.company;
  const companyInformation = company?.company_information;
  const assignee = candidateProfile?.organization_users?.[0];

  return (
    <div className="flex h-full flex-col bg-white">
      <div className="flex h-[57px] shrink-0 items-center gap-1.25 pt-2.25 pl-3.5">
        <button
          type="button"
          onClick={closeSidebar}
          className="flex size-icon-xxs h-10.5 w-auto items-center justify-center rounded-sm border-0 bg-transparent px-sm"
        >
          <img
            src={`/agent/job_change_processes/icon-arrow-right.svg`}
            width={16}
            height={16}
            alt=""
          />
        </button>
        <p className="text-heading-xs text-main">詳細</p>
      </div>
      <div className="hidden-scrollbar min-h-0 flex-1 overflow-y-auto overscroll-contain pr-7.25 pb-6 pl-7">
        <div className="flex flex-col gap-7">
          <DetailSection title="ステータス">
            <JobChangeProcessLogList
              currentSelectjobChangeProcessId={currentSelectjobChangeProcess!.id}
              logs={data?.logs ?? []}
              statusOpen={statusOpen}
              refetch={refetch}
              isSidebarOpen={isSidebarOpen}
              createdAt={data.created_at}
            />
          </DetailSection>

          <DetailSection title="求職者情報">
            <div className="flex w-full flex-col gap-xxs">
              <div className="flex flex-col gap-sm">
                <Link
                  to={candidateProfile.url}
                  className="text-heading-xs text-primary hover:underline"
                >
                  {candidateProfile.name}
                </Link>
                <div className="flex flex-col gap-1.5">
                  <DetailField label="担当者" labelWidth="w-[66px]">
                    {assignee ? (
                      <span className="inline-flex h-4.25 items-center rounded-full bg-tag-gray px-2.5 text-[10px] text-tag-gray">
                        {assignee.name}
                      </span>
                    ) : (
                      <span>-</span>
                    )}
                  </DetailField>
                  <DetailField label="勤務会社">{candidateProfile.company_name ?? '-'}</DetailField>
                  <DetailField label="職種">{candidateProfile.job_position ?? '-'}</DetailField>
                  {isCandidateDetailOpen && (
                    <>
                      <DetailField label="現住所">
                        {candidateProfile.current_address ?? '-'}
                      </DetailField>
                      <DetailField label="年齢">
                        {candidateProfile.age !== null ? `${candidateProfile.age}歳` : '-'}
                      </DetailField>
                      <DetailField label="現年収">
                        {candidateProfile.current_salary ?? '-'}
                      </DetailField>
                      <DetailField label="希望年収">
                        {candidateProfile.desired_salary ?? '-'}
                      </DetailField>
                      <DetailField label="転職希望時期">
                        {candidateProfile.desired_job_change ?? '-'}
                      </DetailField>
                      <DetailField label="メール">{candidateProfile.email ?? '-'}</DetailField>
                    </>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsCandidateDetailOpen((prev) => !prev)}
                className="flex w-full items-center justify-center border-0 bg-transparent p-0"
                aria-expanded={isCandidateDetailOpen}
                aria-label={
                  isCandidateDetailOpen ? '求職者の追加情報を閉じる' : '求職者の追加情報を開く'
                }
              >
                {isCandidateDetailOpen ? (
                  <MdKeyboardArrowUp className="size-icon-xxs text-main" />
                ) : (
                  <MdKeyboardArrowDown className="size-icon-xxs text-main" />
                )}
              </button>
            </div>
          </DetailSection>

          <DetailSection title="求人情報">
            <div className="flex w-full flex-col gap-2.5">
              {jobUrl ? (
                <Link to={jobUrl} className="text-heading-xs text-primary hover:underline">
                  {jobTitle ?? '求人'}
                </Link>
              ) : (
                <p className="text-heading-xs text-primary">{jobTitle ?? '求人'}</p>
              )}
              <div className="flex flex-col gap-1.5">
                <DetailField label="職種">{providedJobPost?.job_position_name ?? '-'}</DetailField>
                <DetailField label="年収例">{providedJobPost?.salary_info ?? '-'}</DetailField>
                <DetailField label="勤務地">{providedJobPost?.working_location ?? '-'}</DetailField>
              </div>
            </div>
          </DetailSection>

          <DetailSection title="企業情報">
            <div className="flex w-full flex-col gap-sm">
              {company ? (
                <Link
                  to="/agent/companies/$companiesId"
                  params={{ companiesId: company.id.toString() }}
                  className="text-heading-xs text-primary hover:underline"
                >
                  {company.name}
                </Link>
              ) : (
                <p className="text-heading-xs text-primary">企業</p>
              )}
              <div className="flex w-full gap-lg">
                <div className="flex w-42 shrink-0 flex-col gap-1.5">
                  <DetailField label="代表者名">
                    {companyInformation?.president_name ?? '-'}
                  </DetailField>
                  <DetailField label="URL">
                    {companyInformation?.official_url ? (
                      <a
                        href={companyInformation.official_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-link hover:underline"
                      >
                        URL
                      </a>
                    ) : (
                      '-'
                    )}
                  </DetailField>
                  <DetailField label="住所">
                    <span className="line-clamp-1">{companyInformation?.address ?? '-'}</span>
                  </DetailField>
                  <DetailField label="設立">
                    {companyInformation?.established_date ?? '-'}
                  </DetailField>
                </div>
                <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                  <DetailField label="フェーズ" labelWidth="w-[72px]">
                    {companyInformation?.phase ?? '-'}
                  </DetailField>
                  <DetailField label="上場情報" labelWidth="w-[72px]">
                    {companyInformation?.listed_phase ?? '-'}
                  </DetailField>
                  <DetailField label="業種" labelWidth="w-[72px]">
                    {companyInformation?.industry ?? '-'}
                  </DetailField>
                  <DetailField label="従業員数" labelWidth="w-[72px]">
                    {companyInformation?.number_of_employees !== null &&
                    companyInformation?.number_of_employees !== undefined
                      ? `${companyInformation.number_of_employees}名`
                      : '-'}
                  </DetailField>
                </div>
              </div>
            </div>
          </DetailSection>
        </div>
      </div>
      {onRollback && canRollback && (
        <div className="flex justify-end border-t border-border px-7 py-4">
          <Button
            color="danger-secondary"
            size="md"
            onClick={async () => {
              if (
                await confirm({
                  title: '提案まで戻す',
                  message:
                    'このマッチングのステータスを提案まで戻しますか？マッチングデータは復元できません。',
                })
              ) {
                onRollback();
              }
            }}
            disabled={isRollbackPending}
          >
            提案まで戻す
          </Button>
        </div>
      )}
    </div>
  );
}
